import { Platform } from 'react-native';
import * as FileSystem from 'expo-file-system';
import * as Sharing from 'expo-sharing';
import api from './api';
import { AuditAction, AuditLogEntry, AuditSummary, PageResponse } from '../types';

export interface AuditFilters {
  q?: string;
  username?: string;
  actions?: AuditAction[];
  succes?: boolean;
  /** ISO-8601 instants. */
  from?: string;
  to?: string;
}

const toParams = (filters: AuditFilters) => ({
  q: filters.q || undefined,
  username: filters.username || undefined,
  // Spring binds a comma-separated value to List<AuditAction>.
  actions: filters.actions && filters.actions.length > 0 ? filters.actions.join(',') : undefined,
  succes: filters.succes,
  from: filters.from,
  to: filters.to,
});

type WebGlobal = {
  URL?: { createObjectURL: (blob: unknown) => string; revokeObjectURL: (url: string) => void };
  document?: {
    createElement: (tagName: 'a') => {
      href: string;
      setAttribute: (name: string, value: string) => void;
      click: () => void;
      remove: () => void;
    };
    body: { appendChild: (node: unknown) => void };
  };
  Blob?: new (parts: string[], options: { type: string }) => unknown;
};

export const auditService = {
  getPage: (page: number, size: number, filters: AuditFilters) =>
    api
      .get<PageResponse<AuditLogEntry>>('/api/audit-logs', { params: { page, size, ...toParams(filters) } })
      .then((r) => r.data),

  getUsernames: () => api.get<string[]>('/api/audit-logs/utilisateurs').then((r) => r.data),

  getSummary: () => api.get<AuditSummary>('/api/audit-logs/resume').then((r) => r.data),

  exportCsv: async (filters: AuditFilters) => {
    const fileName = `historique-actions-${new Date().toISOString().slice(0, 10)}.csv`;
    const response = await api.get<string>('/api/audit-logs/export.csv', {
      params: toParams(filters),
      responseType: 'text',
      timeout: 60000,
    });
    const content = response.data ?? '';

    if (Platform.OS === 'web') {
      const webGlobal = globalThis as unknown as WebGlobal;
      const urlApi = webGlobal.URL;
      const doc = webGlobal.document;
      const BlobCtor = webGlobal.Blob;
      if (!urlApi || !doc || !BlobCtor) {
        throw new Error('Téléchargement non supporté sur cet environnement web.');
      }
      const url = urlApi.createObjectURL(new BlobCtor([content], { type: 'text/csv;charset=utf-8' }));
      const link = doc.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      doc.body.appendChild(link);
      link.click();
      link.remove();
      urlApi.revokeObjectURL(url);
      return;
    }

    const directory = FileSystem.documentDirectory ?? FileSystem.cacheDirectory;
    if (!directory) {
      throw new Error('Espace de stockage indisponible sur cet appareil.');
    }
    const fileUri = `${directory}${fileName}`;
    await FileSystem.writeAsStringAsync(fileUri, content, { encoding: FileSystem.EncodingType.UTF8 });
    if (await Sharing.isAvailableAsync()) {
      await Sharing.shareAsync(fileUri, {
        mimeType: 'text/csv',
        dialogTitle: `Enregistrer ${fileName}`,
        UTI: 'public.comma-separated-values-text',
      });
    }
  },
};
